import { ChevronRight, FileText, Image as ImageIcon, Table as TableIcon, ExternalLink, Loader2, ImageOff, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { useAuthedImage } from "../../hooks/useAuthedImage";
import Md from "../Md";
import { cleanSnippet } from "../snippetClean";

const TABLE_ALLOWED_TAGS = ["table", "thead", "tbody", "tr", "th", "td", "br"];
const TABLE_ALLOWED_ATTR = ["colspan", "rowspan"];

export function sanitizeTableSnippet(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: TABLE_ALLOWED_TAGS,
    ALLOWED_ATTR: TABLE_ALLOWED_ATTR,
  });
}

// Markdown-image refs aus den brain_store chunks haben keine auflösbaren src —
// vor dem <Md>-Render rauswerfen, sonst zeigt der Browser broken images.
// Markdown-Link-Stubs auf Tabellen ([c0-tbl-N.html](c0-tbl-N.html)) gehören in
// Source-Snippets auch nicht hin — der User sieht die Tabelle als eigene
// Source-Card.
function stripEmbeddedRefs(md: string): string {
  return md
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/\[[^\]]*-tbl-\d+\.html\]\([^)]*\)/g, "");
}

interface CommonProps {
  msgId: string;
  n: number;
  isHi: boolean;
  documentId: string;
  documentTitle: string;
  defaultOpen?: boolean;
}

// Combine `defaultOpen` (initial render) with `isHi` (cite-click forces open)
// without locking the user out — they can still toggle the details element
// manually after.
function useCardOpen(defaultOpen: boolean | undefined, isHi: boolean) {
  const [open, setOpen] = useState(Boolean(defaultOpen));
  useEffect(() => {
    if (isHi) setOpen(true);
  }, [isHi]);
  return [open, setOpen] as const;
}

/** Source-Card für externe Vektor-Quellen (Supabase). Keine lokale doc-Seite
 *  zum Springen — daher ohne DocLink. Visuell von Text-Sources unterschieden
 *  über das Sparkles-Icon. */
export function ExternalSourceCard({
  msgId, n, isHi, documentTitle, content, defaultOpen,
}: Omit<CommonProps, "documentId"> & { content: string }) {
  const [open, setOpen] = useCardOpen(defaultOpen, isHi);
  return (
    <details
      id={`src-${msgId}-${n}`}
      open={open}
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
      className={cardClasses(isHi)}
    >
      <summary className="px-3 py-2 cursor-pointer flex items-center gap-2 list-none">
        <Sparkles className="w-3.5 h-3.5 text-ink-50/60" />
        <SourceNumber n={n} />
        <DocTitle title={documentTitle} />
        <span className="text-[10px] text-ink-50/50 mr-1">extern</span>
        <ChevronRight className="w-3 h-3 text-ink-50/40 group-open:rotate-90 transition-transform" />
      </summary>
      <div className="px-3 pb-3 text-xs text-ink-50/80 leading-relaxed [&_table]:text-[11px]">
        <Md>{stripEmbeddedRefs(cleanSnippet(content))}</Md>
      </div>
    </details>
  );
}


export function TextSourceCard({
  msgId, n, isHi, documentId, documentTitle, content, defaultOpen,
}: CommonProps & { content: string }) {
  const [open, setOpen] = useCardOpen(defaultOpen, isHi);
  return (
    <details
      id={`src-${msgId}-${n}`}
      open={open}
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
      className={cardClasses(isHi)}
    >
      <summary className="px-3 py-2 cursor-pointer flex items-center gap-2 list-none">
        <FileText className="w-3.5 h-3.5 text-ink-50/60" />
        <SourceNumber n={n} />
        <DocTitle title={documentTitle} />
        <ChevronRight className="w-3 h-3 text-ink-50/40 group-open:rotate-90 transition-transform" />
      </summary>
      <div className="px-3 pb-3 text-xs text-ink-50/80 leading-relaxed [&_table]:text-[11px]">
        <Md>{stripEmbeddedRefs(cleanSnippet(content))}</Md>
        <DocLink documentId={documentId} />
      </div>
    </details>
  );
}

export function FigureSourceCard({
  msgId, n, isHi, documentId, documentTitle, figId, page, caption, defaultOpen,
  sourceHash, externalSourceSlug,
}: CommonProps & {
  figId: string; page: number; caption: string | null;
  // Bei external sources beide gesetzt → URL geht über /projects/external-sources/
  sourceHash?: string | null;
  externalSourceSlug?: string | null;
}) {
  const [open, setOpen] = useCardOpen(defaultOpen, isHi);
  const figurePath = externalSourceSlug && sourceHash
    ? `/projects/external-sources/${externalSourceSlug}/figures/${sourceHash}/${encodeURIComponent(figId)}`
    : `/documents/${documentId}/figures/${encodeURIComponent(figId)}`;
  const { src, loading, error } = useAuthedImage(open ? figurePath : null);
  return (
    <details
      id={`src-${msgId}-${n}`}
      open={open}
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
      className={cardClasses(isHi)}
    >
      <summary className="px-3 py-2 cursor-pointer flex items-center gap-2 list-none">
        <ImageIcon className="w-3.5 h-3.5 text-ink-50/60" />
        <SourceNumber n={n} />
        <DocTitle title={documentTitle} />
        <span className="text-[10px] text-ink-50/50 mr-1">Bild · Seite {page}</span>
        <ChevronRight className="w-3 h-3 text-ink-50/40 group-open:rotate-90 transition-transform" />
      </summary>
      <div className="px-3 pb-3 space-y-2">
        <div className="bg-cream-50 dark:bg-ink-900/40 rounded-md border border-cream-200 dark:border-ink-700 min-h-[160px] flex items-center justify-center p-2">
          {loading && <Loader2 className="w-5 h-5 text-ink-50/40 animate-spin" />}
          {error && (
            <div className="text-[11px] text-ink-50/50 inline-flex items-center gap-1.5">
              <ImageOff className="w-4 h-4" /> Bild nicht verfügbar
            </div>
          )}
          {src && (
            <img
              src={src}
              alt={caption ?? `Bild auf Seite ${page}`}
              loading="lazy"
              className="max-w-full max-h-[360px] w-auto h-auto object-contain"
            />
          )}
        </div>
        {caption && (
          <p className="text-[12px] text-ink-50/80 leading-snug">
            {caption}
          </p>
        )}
        <DocLink documentId={documentId} />
      </div>
    </details>
  );
}

export function TableSourceCard({
  msgId, n, isHi, documentId, documentTitle, page, caption, summary, htmlPreview, defaultOpen,
}: CommonProps & {
  page: number;
  caption: string | null;
  summary: string | null;
  htmlPreview: string | null;
}) {
  const [open, setOpen] = useCardOpen(defaultOpen, isHi);
  const cleanHtml = htmlPreview ? sanitizeTableSnippet(htmlPreview) : null;
  return (
    <details
      id={`src-${msgId}-${n}`}
      open={open}
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
      className={cardClasses(isHi)}
    >
      <summary className="px-3 py-2 cursor-pointer flex items-center gap-2 list-none">
        <TableIcon className="w-3.5 h-3.5 text-ink-50/60" />
        <SourceNumber n={n} />
        <DocTitle title={documentTitle} />
        <span className="text-[10px] text-ink-50/50 mr-1">Tabelle · Seite {page}</span>
        <ChevronRight className="w-3 h-3 text-ink-50/40 group-open:rotate-90 transition-transform" />
      </summary>
      <div className="px-3 pb-3 space-y-2 text-xs text-ink-50/80">
        {caption && (
          <p className="italic leading-snug">{caption}</p>
        )}
        {summary && <p className="leading-relaxed">{summary}</p>}
        {!summary && cleanHtml && (
          <div
            className="overflow-x-auto rounded-md border border-cream-200 dark:border-ink-600 bg-surface/60 dark:bg-ink-900/50 [&_table]:w-full [&_table]:text-[11px] [&_table]:border-collapse [&_th]:bg-cream-100 [&_th]:dark:bg-ink-800 [&_th]:font-medium [&_th]:text-left [&_th]:p-1.5 [&_th]:border [&_th]:border-cream-200 [&_th]:dark:border-ink-700 [&_td]:p-1.5 [&_td]:border [&_td]:border-cream-200 [&_td]:dark:border-ink-700 [&_td]:align-top"
            dangerouslySetInnerHTML={{ __html: cleanHtml }}
          />
        )}
        {!summary && !cleanHtml && (
          <p className="text-ink-50/50 italic">
            Vollansicht im Dokument.
          </p>
        )}
        <DocLink documentId={documentId} label="Tabelle im Dokument anzeigen" />
      </div>
    </details>
  );
}

function cardClasses(isHi: boolean): string {
  return [
    "bg-cream-50 dark:bg-ink-800/60 border rounded-lg text-sm group transition",
    isHi
      ? "border-brand-400 dark:border-brand-500 ring-2 ring-brand-200/60 dark:ring-brand-700/40"
      : "border-cream-200 dark:border-ink-600",
  ].join(" ");
}

function SourceNumber({ n }: { n: number }) {
  return (
    <span className="text-brand-600 dark:text-brand-400 text-xs font-semibold">
      {n}
    </span>
  );
}

function DocTitle({ title }: { title: string }) {
  return <span className="font-medium truncate flex-1">{title}</span>;
}

function DocLink({ documentId, label = "Im Dokument öffnen" }: { documentId: string; label?: string }) {
  return (
    <Link
      to={`/documents/${documentId}`}
      className="inline-flex items-center gap-0.5 text-[11px] text-brand-600 hover:underline pt-1"
    >
      {label} <ExternalLink className="w-2.5 h-2.5" />
    </Link>
  );
}
